/**
 * Additional Terms on an order form are stored as a small, safe subset of
 * markdown, so forms saved before the rich text editor keep rendering the
 * same way:
 *
 *   ## Heading          a section heading (### is a smaller sub-heading)
 *   Plain line          a paragraph (every non-empty line is one paragraph)
 *   - item / 1. item    bulleted and numbered lists
 *   **bold** *italic* [text](https://link)
 *
 * The editor, the customer document and the PDF all read this one parser.
 * Nothing here produces HTML: the document builds React elements and the PDF
 * draws text, so pasted markup can never reach a page as live HTML. Links
 * keep only http, https and mailto addresses.
 */

export interface TermsInline {
  text: string
  bold?: boolean
  italic?: boolean
  href?: string
}

export type TermsBlock =
  | { type: 'heading'; level: 2 | 3; children: TermsInline[] }
  | { type: 'paragraph'; children: TermsInline[] }
  | { type: 'list'; ordered: boolean; start: number; items: TermsInline[][] }

/** A link the document and the PDF may follow, or null. Adds https:// to a bare domain. */
export function safeTermsHref(value: string | null | undefined): string | null {
  const href = (value || '').trim()
  if (!href || /\s/.test(href)) return null
  if (/^mailto:[^@\s]+@[^@\s]+$/i.test(href)) return href
  if (/^https?:\/\/[^\s/?#]+\.[^\s/?#]+/i.test(href)) return href
  if (/^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?$/i.test(href)) return `https://${href}`
  return null
}

/** The plain text of a run of inline content, for headings in the PDF and for tests. */
export function termsInlineText(children: TermsInline[]): string {
  return children.map((child) => child.text).join('')
}

// ---------------------------------------------------------------------------
// Parsing

const HEADING = /^(#{2,3})\s+(.*)$/
const BULLET = /^[-*+]\s+(.*)$/
const ORDERED = /^(\d{1,9})[.)]\s+(.*)$/

/** Parses stored Additional Terms into blocks. Every non-empty line is its own block. */
export function parseTermsMarkdown(source: string): TermsBlock[] {
  const blocks: TermsBlock[] = []
  for (const raw of (source || '').replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim()
    if (!line) continue
    const heading = line.match(HEADING)
    if (heading) {
      const children = parseTermsInline(heading[2].trim())
      if (children.length) blocks.push({ type: 'heading', level: heading[1].length === 3 ? 3 : 2, children })
      continue
    }
    const bullet = line.match(BULLET)
    const ordered = bullet ? null : line.match(ORDERED)
    if (bullet || ordered) {
      const children = parseTermsInline((bullet ? bullet[1] : ordered![2]).trim())
      const last = blocks[blocks.length - 1]
      if (last && last.type === 'list' && last.ordered === Boolean(ordered)) last.items.push(children)
      else blocks.push({ type: 'list', ordered: Boolean(ordered), start: ordered ? Number(ordered[1]) || 1 : 1, items: [children] })
      continue
    }
    const children = parseTermsInline(line)
    if (children.length) blocks.push({ type: 'paragraph', children })
  }
  return blocks
}

const PUNCTUATION = /[!-/:-@[-`{-~]/

function isSpace(char: string | undefined): boolean {
  return char === undefined || /\s/.test(char)
}

function isPunctuation(char: string | undefined): boolean {
  return char !== undefined && (PUNCTUATION.test(char) || /\p{P}/u.test(char))
}

function matchLink(text: string, start: number): { label: string; href: string; end: number } | null {
  let depth = 0
  for (let index = start; index < text.length; index += 1) {
    const char = text[index]
    if (char === '\\') { index += 1; continue }
    if (char === '[') depth += 1
    if (char === ']') {
      depth -= 1
      if (depth === 0) {
        if (text[index + 1] !== '(') return null
        let close = -1
        let parens = 0
        for (let cursor = index + 2; cursor < text.length; cursor += 1) {
          if (text[cursor] === '(') parens += 1
          else if (text[cursor] === ')') { if (parens === 0) { close = cursor; break } parens -= 1 }
        }
        if (close === -1) return null
        const href = text.slice(index + 2, close).trim()
        if (!href || /\s/.test(href)) return null
        return { label: text.slice(start + 1, index), href, end: close + 1 }
      }
    }
  }
  return null
}

type Delim = { kind: 'delim'; char: '*' | '_'; count: number; length: number; canOpen: boolean; canClose: boolean }
type Span = { kind: 'span'; mark: 'bold' | 'italic' | 'link'; href?: string; children: InlineNode[] }
type InlineNode = { kind: 'text'; text: string } | Delim | Span

/**
 * Tokenizes one line: escapes, links, then runs of `*` and `_` with the
 * CommonMark flanking rules. Emphasis is then resolved with the CommonMark
 * delimiter algorithm, so `**a***b*` reads as bold "a" then italic "b".
 */
function tokenizeInline(text: string, inLink: boolean): InlineNode[] {
  const nodes: InlineNode[] = []
  let buffer = ''
  const flush = () => { if (buffer) nodes.push({ kind: 'text', text: buffer }); buffer = '' }
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '\\' && index + 1 < text.length && PUNCTUATION.test(text[index + 1])) {
      buffer += text[index + 1]
      index += 1
      continue
    }
    if (char === '[' && !inLink) {
      const link = matchLink(text, index)
      if (link) {
        flush()
        const href = safeTermsHref(link.href.replace(/\\([()])/g, '$1'))
        const children = resolveEmphasis(tokenizeInline(link.label, true))
        // An unsafe link keeps its words and drops the address.
        nodes.push(href ? { kind: 'span', mark: 'link', href, children } : { kind: 'span', mark: 'link', children })
        index = link.end - 1
        continue
      }
    }
    if (char === '*' || char === '_') {
      let end = index
      while (text[end] === char) end += 1
      const length = end - index
      const before = text[index - 1]
      const after = text[end]
      const left = !isSpace(after) && (!isPunctuation(after) || isSpace(before) || isPunctuation(before))
      const right = !isSpace(before) && (!isPunctuation(before) || isSpace(after) || isPunctuation(after))
      const canOpen = char === '*' ? left : left && (!right || isPunctuation(before))
      const canClose = char === '*' ? right : right && (!left || isPunctuation(after))
      flush()
      nodes.push({ kind: 'delim', char, count: length, length, canOpen, canClose })
      index = end - 1
      continue
    }
    buffer += char
  }
  flush()
  return resolveEmphasis(nodes)
}

function resolveEmphasis(input: InlineNode[]): InlineNode[] {
  const nodes = [...input]
  let closerIndex = 0
  while (closerIndex < nodes.length) {
    const closer = nodes[closerIndex]
    if (closer.kind !== 'delim' || !closer.canClose || closer.count === 0) { closerIndex += 1; continue }
    let openerIndex = -1
    for (let index = closerIndex - 1; index >= 0; index -= 1) {
      const opener = nodes[index]
      if (opener.kind !== 'delim' || opener.char !== closer.char || !opener.canOpen || opener.count === 0) continue
      const oddMatch = (opener.canClose || closer.canOpen) && (opener.length + closer.length) % 3 === 0 && !(opener.length % 3 === 0 && closer.length % 3 === 0)
      if (oddMatch) continue
      openerIndex = index
      break
    }
    if (openerIndex === -1) { closerIndex += 1; continue }
    const opener = nodes[openerIndex] as Delim
    const use = opener.count >= 2 && closer.count >= 2 ? 2 : 1
    const span: Span = { kind: 'span', mark: use === 2 ? 'bold' : 'italic', children: nodes.slice(openerIndex + 1, closerIndex) }
    nodes.splice(openerIndex + 1, closerIndex - openerIndex - 1, span)
    opener.count -= use
    closer.count -= use
    closerIndex = openerIndex + 2
    if (opener.count === 0) { nodes.splice(openerIndex, 1); closerIndex -= 1 }
    if (closer.count === 0) { nodes.splice(closerIndex, 1) }
  }
  return nodes
}

function flattenInline(nodes: InlineNode[], marks: Omit<TermsInline, 'text'>, out: TermsInline[]) {
  for (const node of nodes) {
    if (node.kind === 'text') out.push({ text: node.text, ...marks })
    else if (node.kind === 'delim') { if (node.count) out.push({ text: node.char.repeat(node.count), ...marks }) }
    else if (node.mark === 'link') flattenInline(node.children, node.href ? { ...marks, href: node.href } : marks, out)
    else flattenInline(node.children, { ...marks, [node.mark]: true }, out)
  }
}

/** Parses one line of inline markdown into styled runs. */
export function parseTermsInline(text: string): TermsInline[] {
  const out: TermsInline[] = []
  flattenInline(tokenizeInline(text, false), {}, out)
  return mergeRuns(out)
}

function sameMarks(left: TermsInline, right: TermsInline): boolean {
  return Boolean(left.bold) === Boolean(right.bold) && Boolean(left.italic) === Boolean(right.italic) && (left.href || '') === (right.href || '')
}

function mergeRuns(runs: TermsInline[]): TermsInline[] {
  const merged: TermsInline[] = []
  for (const run of runs) {
    if (!run.text) continue
    const clean: TermsInline = { text: run.text, ...(run.bold ? { bold: true } : {}), ...(run.italic ? { italic: true } : {}), ...(run.href ? { href: run.href } : {}) }
    const last = merged[merged.length - 1]
    if (last && sameMarks(last, clean)) last.text += clean.text
    else merged.push(clean)
  }
  return merged
}

// ---------------------------------------------------------------------------
// Serializing

function escapeText(text: string): string {
  return text
    .replace(/[\\*[\]]/g, (char) => `\\${char}`)
    // An underscore inside a word ("snake_case") never styles text, so it stays as typed.
    .replace(/_/g, (char, offset: number, whole: string) => /[\p{L}\p{N}]/u.test(whole[offset - 1] || '') && /[\p{L}\p{N}]/u.test(whole[offset + 1] || '') ? char : `\\${char}`)
}

/** Escapes what would otherwise start a heading or a list at the start of a line. */
function escapeLineStart(line: string): string {
  if (/^#{2,3}\s/.test(line)) return `\\${line}`
  if (/^[-+]\s/.test(line)) return `\\${line}`
  const ordered = line.match(/^(\d{1,9})([.)])(\s)/)
  if (ordered) return `${ordered[1]}\\${line.slice(ordered[1].length)}`
  return line
}

type Mark = 'bold' | 'italic'
const MARKER: Record<Mark, string> = { bold: '**', italic: '*' }

function serializeMarks(runs: TermsInline[]): string {
  let out = ''
  const stack: Mark[] = []
  const marksOf = (run: TermsInline | undefined): Mark[] => run ? (['bold', 'italic'] as Mark[]).filter((mark) => run[mark]) : []
  // Closes from the innermost mark until every mark still open is in `keep`.
  const closeTo = (keep: Mark[]) => {
    let depth = 0
    while (depth < stack.length && keep.includes(stack[depth])) depth += 1
    while (stack.length > depth) out += MARKER[stack.pop()!]
  }
  runs.forEach((run, index) => {
    const lead = run.text.match(/^\s*/)![0]
    const rest = run.text.slice(lead.length)
    const trail = rest.match(/\s*$/)![0]
    const core = rest.slice(0, rest.length - trail.length)
    const wanted = marksOf(run)
    if (!core) { closeTo(wanted); out += run.text; return }
    // A marker always touches a word: marks that end close before the
    // space, marks that start open after it, marks that continue keep it.
    closeTo(wanted)
    out += lead
    for (const mark of wanted) if (!stack.includes(mark)) { out += MARKER[mark]; stack.push(mark) }
    out += escapeText(core)
    if (trail) { closeTo(marksOf(runs[index + 1])); out += trail }
  })
  closeTo([])
  return out
}

function serializeInline(children: TermsInline[]): string {
  // Group runs by link: a link wraps its styled text, `[**Rate** card](url)`.
  let out = ''
  let index = 0
  while (index < children.length) {
    const href = children[index].href
    let end = index
    while (end < children.length && children[end].href === href) end += 1
    const group = children.slice(index, end)
    const safe = href ? safeTermsHref(href) : null
    const body = serializeMarks(group.map(({ href: _href, ...rest }) => rest))
    out += safe ? `[${body}](${safe.replace(/[()\s]/g, (char) => encodeURIComponent(char))})` : body
    index = end
  }
  return out.replace(/\n/g, ' ')
}

/** Writes blocks back to stored markdown. One block per line, a blank line between blocks. */
export function serializeTermsMarkdown(blocks: TermsBlock[]): string {
  const lines: string[] = []
  for (const block of blocks) {
    if (block.type === 'heading') {
      const text = serializeInline(block.children).trim()
      if (text) lines.push(`${block.level === 3 ? '###' : '##'} ${text}`)
    } else if (block.type === 'paragraph') {
      const text = serializeInline(block.children).trim()
      if (text) lines.push(escapeLineStart(text))
    } else {
      const items = block.items.map((item) => serializeInline(item).trim()).filter(Boolean)
      if (!items.length) continue
      lines.push(items.map((item, position) => `${block.ordered ? `${block.start + position}.` : '-'} ${item}`).join('\n'))
    }
  }
  return lines.join('\n\n')
}

// ---------------------------------------------------------------------------
// TipTap (ProseMirror JSON) bridge for the editor

export type JsonNode = { type?: string; text?: string; attrs?: Record<string, unknown>; marks?: Array<{ type: string; attrs?: Record<string, unknown> }>; content?: JsonNode[] }

function inlineToTiptap(children: TermsInline[]): JsonNode[] {
  return children.filter((child) => child.text).map((child) => {
    const marks = [
      ...(child.bold ? [{ type: 'bold' }] : []),
      ...(child.italic ? [{ type: 'italic' }] : []),
      ...(child.href ? [{ type: 'link', attrs: { href: child.href } }] : []),
    ]
    return { type: 'text', text: child.text, ...(marks.length ? { marks } : {}) }
  })
}

/** Stored markdown to a TipTap document. */
export function termsToTiptap(source: string): JsonNode {
  const content: JsonNode[] = parseTermsMarkdown(source).map((block) => {
    if (block.type === 'heading') return { type: 'heading', attrs: { level: block.level }, content: inlineToTiptap(block.children) }
    if (block.type === 'paragraph') return { type: 'paragraph', content: inlineToTiptap(block.children) }
    return {
      type: block.ordered ? 'orderedList' : 'bulletList',
      ...(block.ordered ? { attrs: { start: block.start } } : {}),
      content: block.items.map((item) => ({ type: 'listItem', content: [{ type: 'paragraph', content: inlineToTiptap(item) }] })),
    }
  })
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] }
}

function tiptapInline(nodes: JsonNode[] | undefined): TermsInline[] {
  const runs: TermsInline[] = []
  for (const node of nodes || []) {
    if (node.type === 'hardBreak') { runs.push({ text: ' ' }); continue }
    if (node.type !== 'text' || !node.text) continue
    const marks = node.marks || []
    const href = safeTermsHref(String(marks.find((mark) => mark.type === 'link')?.attrs?.href || ''))
    runs.push({ text: node.text, ...(marks.some((mark) => mark.type === 'bold') ? { bold: true } : {}), ...(marks.some((mark) => mark.type === 'italic') ? { italic: true } : {}), ...(href ? { href } : {}) })
  }
  return mergeRuns(runs)
}

/** List items flatten to one level: nested lists become items of the list they sit in. */
function tiptapListItems(list: JsonNode, items: TermsInline[][]) {
  for (const item of list.content || []) {
    for (const child of item.content || []) {
      if (child.type === 'paragraph' || child.type === 'heading') {
        const runs = tiptapInline(child.content)
        if (runs.length) items.push(runs)
      } else if (child.type === 'bulletList' || child.type === 'orderedList') {
        tiptapListItems(child, items)
      }
    }
  }
}

function tiptapBlocks(nodes: JsonNode[] | undefined, blocks: TermsBlock[]) {
  for (const node of nodes || []) {
    if (node.type === 'heading') {
      const children = tiptapInline(node.content)
      if (children.length) blocks.push({ type: 'heading', level: Number(node.attrs?.level) === 3 ? 3 : 2, children })
    } else if (node.type === 'paragraph') {
      // A soft line break inside a paragraph starts a new paragraph, the
      // way every stored line is its own paragraph.
      let current: JsonNode[] = []
      const push = () => { const children = tiptapInline(current); if (children.length) blocks.push({ type: 'paragraph', children }); current = [] }
      for (const child of node.content || []) {
        if (child.type === 'hardBreak') push()
        else current.push(child)
      }
      push()
    } else if (node.type === 'bulletList' || node.type === 'orderedList') {
      const items: TermsInline[][] = []
      tiptapListItems(node, items)
      if (items.length) blocks.push({ type: 'list', ordered: node.type === 'orderedList', start: node.type === 'orderedList' ? Math.max(1, Number(node.attrs?.start) || 1) : 1, items })
    } else if (node.content) {
      tiptapBlocks(node.content, blocks)
    }
  }
}

/** A TipTap document back to stored markdown. */
export function tiptapToTerms(doc: unknown): string {
  const blocks: TermsBlock[] = []
  if (doc && typeof doc === 'object') tiptapBlocks((doc as JsonNode).content, blocks)
  return serializeTermsMarkdown(blocks)
}
